	import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
	import Button from '../button';
	import DropDownFields from '../drop-down/DropdownField';
	import TextField from '@mui/material/TextField';
	import PrimaryButton from '../button/PrimaryButton';
	import IconButton from '@mui/material/IconButton';
	import DatePickerExpanded from '../date-picker/DatePickerExpanded';
	import dayjs, { Dayjs } from 'dayjs';
	import MultipleLineTextField from '../text-field/Multiple';
	import ApiService from '../../utils/services/ApiService';
	import {
		DataPointTestKitMeasurement,
		TestKitMeasurementResultValueVO,
		TestKitMeasurementResultVO,
	} from 'utils/models/test-kit-measurement';
	import DeleteConfirmationDialog from '../dialog/Delete';
	import CancelConfirmationDialog from '../dialog/Cancel';
	import { useConnection } from '../../utils/helper/ConnectionContext';
	import { responseEmitter } from '../../utils/usb/USBConnector';
	import 'react-toastify/dist/ReactToastify.css';
	import LoadingPopup from '../dialog/LoadingBar';
	import TextFieldExpanded from '../text-field/TextFieldExpanded';
	import {
		saveTestResults,
		getTestResults,
		saveSyncCache,
	} from 'utils/services/idbService';
	import { getSwitchStateFromDB } from '../../utils/services/idbService';
	import { Autocomplete, CircularProgress } from '@mui/material';
	import { usePopupMessage } from '../../hooks/SendPopUpMessage';
	import { RiskScoreVO } from 'utils/models/risk-score';
	import { formatNumber } from 'utils/functions/formatNumber';
	import { evaluateFormula, separateRequiredOptionalFields } from 'utils/functions/evaluateFormula';
	import { SiteAlarmSetting } from 'utils/models/location-alarms';
	import { checkForOptionalBackgroundButton, isBehindCalibration } from 'utils/functions/testAndEditHelpers';
	import { TestSelection } from '../../types/testSelection';
	import { addEventLog } from 'utils/functions/eventLogFunctions';
	import ReadButton from '../button/ReadButton';
	import CONSTANTS from '../../utils/constants/UIConstants';

	interface Location {
		id: number;
		name: string;
		testKitDescriptors: {
			testKitName: string;
			id: number;
			riskScoreVO?: RiskScoreVO;
		}[];
	}

	interface TestProps {
		addStyle?: string;
		onAddTestClick: () => void;
		onRemoveTestClick: () => void;
		index: number;
		canRemove: boolean;
		locations?: Location[];
		alarmSettingsPerLocation?: SiteAlarmSetting[];
		calibrationData: { [inputId: string]: number };
		userId?: number;
		onTestDone?: (samplePointId?: number) => void;
		onMissingFieldsUpdate?: (missing: string[]) => void;
		siteId: number;
		siteName?: string;
		onSelectionComplete?: (selection: TestSelection) => void;
		isTestLoading: boolean;
		setIsTestLoading: (loading: boolean) => void;
		shouldClear?: boolean;
	}

	export interface TestKitInputDescriptor {
		id: number;
		inputId: string;
		inputName: string;
		unit: string;
		minimum: number;
		toPrev: boolean;
		readPM: boolean;
		isRequired: boolean;
		defaultValue: number;
	} 
	interface TestKitOutputDescriptor {
		id: number;
		outputName: string;
		outputId: string;
		unit: string;
		maxVal: number;
		minVal: number;
		toPrev: boolean;
		formula: string;
		hide: boolean;
		hiValue?: number;
		hiHiValue?: number;
		loValue?: number;
		loLoValue?: number;
	hiAlarmValue?: number;
	hiHiAlarmValue?: number;
	loAlarmValue?: number;
	loLoAlarmValue?: number;
		// ... add other fields as necessary
	}
	const Test: React.FC<TestProps> = ({
		addStyle = '',
		onAddTestClick,
		index,
		onRemoveTestClick,
		locations = [],
		alarmSettingsPerLocation = [],
		calibrationData,
		userId,
		onTestDone,
		onMissingFieldsUpdate,
		siteId,
		siteName,
		onSelectionComplete,
		isTestLoading,
		setIsTestLoading,
		shouldClear = false,
	}) => {
		const sendPopUpMessage = usePopupMessage();
		const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
		const [selectedLocation, setSelectedLocation] = useState<string>('');
		const locationOptions = locations?.map((loc) => loc.name) ?? [];
		const [value, setValue] = useState('');
		const [descriptionValue, setDescriptionValue] = useState('');
		const [tATPValue, setTATPValue] = useState('');
		const [textValue, setTextValue] = useState('');
		const [hovered, setHovered] = useState(false);
		const [selectedTestKit, setSelectedTestKit] = useState<string>('');
		const [selectedLocationDetails, setSelectedLocationDetails] =
			useState<Location | null>(null);

		const [availableTestKits, setAvailableTestKits] = useState<string[]>([]);
		const [loading, setLoading] = useState(false);
		const [isSaving, setIsSaving] = useState(false);
		const [isUploaded, setIsUploaded] = useState(false);
		const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
		const [isCancelTestDialogOpen, setIsCancelTestDialogOpen] = useState(false);
		const [showDialog, setShowDialog] = useState(false);
		const [clearTimeoutId, setClearTimeoutId] = useState<number | null>(null);
		const { isConnected, setIsConnected } = useConnection();
		const isPBM = localStorage.getItem('connectedDeviceType') === 'PBM'; 
		const getDeviceIds = (): { deviceID?: string; pbmId?: string } => {
			if (!isConnected) return {};
			const isPBM = localStorage.getItem('connectedDeviceType') === 'PBM';
			if (isPBM) {
				return {
					deviceID: localStorage.getItem('PMSerialNumber') ?? undefined,
					pbmId: checkPBMSerialNum() ?? undefined,
				};
			}
			return {
				deviceID: localStorage.getItem('analyzerSerialNumber') ?? undefined,
				pbmId: undefined, // an analyzer never has a PBM
			};
		};
		const [touchedFields, setTouchedFields] = useState({});
		const [backgroundsOn, setBackgroundsOn] = useState(false);
		
		const setFieldTouched = (fieldId: any) => {
			setTouchedFields((prev) => ({ ...prev, [fieldId]: true }));
		};

		const openDeleteDialog = () => {
			setIsDeleteDialogOpen(true);
		};

		const closeDeleteDialog = () => {
			setIsDeleteDialogOpen(false);
		};
		const handleDelete = () => {
			onRemoveTestClick();
			closeDeleteDialog();
		};

		const handleCancelTest = () => {
			onRemoveTestClick();
			closeCancelTestDialog();
			addEventLog("Run Test Cancelled", "Tests", siteId);
		};
		
		const openCacelTestDialog = () => {
			setIsCancelTestDialogOpen(true);
		};

		const closeCancelTestDialog = () => {
			setIsCancelTestDialogOpen(false);
		};
		useEffect(() => {
			if (selectedLocation && selectedTestKit && onSelectionComplete) {
				onSelectionComplete({
					index: index,
					location: selectedLocation,
					testKit: selectedTestKit,
					outputValues: outputValues,
				});
			}
		}, [selectedLocation, selectedTestKit, onSelectionComplete]);

		
		const testKitDetails = useMemo(() => {
			const fetchedDetails: Record<number, any> = {};
			for (const location of locations) {
				for (const testKit of location.testKitDescriptors) {
					if (!fetchedDetails[testKit.id]) {
						fetchedDetails[testKit.id] = testKit;
					}
				}
			}
			return fetchedDetails;
		}, [locations]);

		const testKitId = locations
			?.find((loc) => loc.name === selectedLocation)
			?.testKitDescriptors?.find(
				(tk) => tk.testKitName === selectedTestKit
			)?.id;
		const riskScoreVO = locations
			?.find((loc) => loc.name === selectedLocation)
			?.testKitDescriptors?.find(
				(tk) => tk.testKitName === selectedTestKit
			)?.riskScoreVO;
	const testKitInputDescriptorVOs = useMemo(() =>
		testKitId && testKitDetails[testKitId]
			? [...testKitDetails[testKitId].testKitInputDescriptorVOs]
				.sort((a: any, b: any) => (a.sortorder ?? 0) - (b.sortorder ?? 0))
			: []
	, [testKitId, testKitDetails]);
		const testKitOutputDescriptorVOs = useMemo(() =>
		testKitId && testKitDetails[testKitId]
			? [...testKitDetails[testKitId].testKitOutputDescriptorVOs]
				.sort((a: any, b: any) => (a.sortorder ?? 0) - (b.sortorder ?? 0))
			: []
	, [testKitId, testKitDetails]);

		const [outputValues, setOutputValues] = useState<Record<string, number>>(
			{}
		);
		const [missingFields, setMissingFields] = useState<string[]>([]);
		const SAMPLE_SIZE = 40;
		const [progress, setProgress] = useState<number>(0);
		const [deviceStatus, setDeviceStatus] = useState<"starting" | "reading" | "completed" | undefined>('starting');
		const { pbmSerialNumber } = useConnection();
		const cleanupMemory = (handleData: any) => {
			responseEmitter.removeListener('data', handleData);
			setProgress(0);
			setShowDialog(false);
			setDeviceStatus('starting');
			if(clearTimeoutId !== null){
				clearTimeout(clearTimeoutId);
				clearInterval(clearTimeoutId);
				setClearTimeoutId(null);
			}
			
		}
		const updateProgressBar = async (progressz: number) => {
				let progressPercent = Math.min((progressz / SAMPLE_SIZE) * 100, 100); // Assuming 40 samples for full progress
				setProgress(prev => progressPercent);
				setDeviceStatus(prev => 'reading');
		};

		const initialInputValues = testKitInputDescriptorVOs.reduce(
			(
				acc: { [x: string]: any },
				descriptor: { defaultValue: undefined; inputId: string | number }
			) => {
				if (descriptor.defaultValue !== undefined) {
					acc[descriptor.inputId] = descriptor.defaultValue;
				}
				return acc;
			},
			{}
		);

		const [inputValues, setInputValues] =
			useState<Record<string, number>>(initialInputValues);

		useEffect(() => {
			if (shouldClear) {
				setInputValues(initialInputValues);
				setSelectedDate(dayjs());
				setDescriptionValue('');
				setSelectedLocation('');
			}
		}, [shouldClear, initialInputValues]);

		useEffect(() => {
			setInputValues(initialInputValues);
		}, [selectedTestKit, selectedLocation]);

		useEffect(() => {
			// Initialize or update input values based on testKitInputDescriptorVOs
			const newInitialInputValues = testKitInputDescriptorVOs.reduce(
				(
					acc: { [x: string]: any },
					descriptor: {
						defaultValue: undefined;
						inputId: string | number;
					}
				) => {
					if (descriptor.defaultValue !== undefined) {
						acc[descriptor.inputId] = descriptor.defaultValue;
					}
					return acc;
				},
				{}
			);

			setInputValues(newInitialInputValues);
		}, [testKitInputDescriptorVOs]);

		const prevCalibrationDataRef = useRef<Record<string, number>>({});
		const prevInputDescriptorsRef = useRef<any[]>([]);
		const outputValuesRef = useRef(outputValues);

		const evaluationConst = useCallback((
				formula: string,
				inputValues: Record<string, number>,
				outputValues: Record<string, number>,
				logRoundDown: boolean = false,
				requiredFields: string[] = [],
				optionalFields: string[] = [],
				minVal?: number,
				maxVal?: number
			): number => {
				return evaluateFormula(formula, inputValues, outputValues, logRoundDown, requiredFields, optionalFields, minVal, maxVal);
		}, []);
		
		const updateOutputValuesBasedOnInput = useCallback((
		newInputValues: Record<string, number>,
		currentOutputValues: Record<string, number>
	) => {
		const newOutputValues: Record<string, number> = {};
		//check required input fields and pass to formula evaluator
		const inputDescriptors = testKitInputDescriptorVOs;
		const { requiredFields, optionalFields } = separateRequiredOptionalFields(inputDescriptors);

		testKitOutputDescriptorVOs.forEach((descriptor: TestKitOutputDescriptor) => {
			try {
				if (descriptor.outputId === 'RiskScore') {
					newOutputValues[descriptor.outputId] = evaluateRiskScore(
						newInputValues,
						{ ...currentOutputValues, ...newOutputValues }
					);
				} else {
					// Updated the formula evaluation to include currentOutputValues
					let logRoundDown = false;
						if (descriptor.outputName.includes("Log #")) {
							logRoundDown = true;
						}
					newOutputValues[descriptor.outputId] = evaluationConst(
						descriptor.formula,
						newInputValues,
						{ ...currentOutputValues, ...newOutputValues },
						logRoundDown,
						requiredFields,
						optionalFields,
						descriptor.minVal,
						descriptor.maxVal
					);
				}
			} catch (error) {
				// console.error("An error occurred during formula evaluation:", error);
			}
		});
		setOutputValues(newOutputValues);
	}, [testKitInputDescriptorVOs, testKitOutputDescriptorVOs, evaluationConst, riskScoreVO]);

		useEffect(() => {
			const newMissingFields = testKitInputDescriptorVOs
				.filter((descriptor: any) => {
					const inputValue = inputValues[descriptor.inputId];
					return (inputValue === null || inputValue === undefined) && descriptor.isRequired;
				})
				.map((descriptor: any) => descriptor.inputId);

			if (!arraysEqual(newMissingFields, missingFields)) {
				setMissingFields(newMissingFields);
				if (onMissingFieldsUpdate) {
					onMissingFieldsUpdate(newMissingFields);
				}
			}
		}, [
			inputValues,
			testKitInputDescriptorVOs,
			onMissingFieldsUpdate,
			missingFields,
		]);

		function arraysEqual(a: any, b: any) {
			if (a === b) return true;
			if (a == null || b == null) return false;
			if (a.length !== b.length) return false;

			for (let i = 0; i < a.length; ++i) {
				if (a[i] !== b[i]) return false;
			}
			return true;
		}

		const updateInputValue = (
			descriptor: TestKitInputDescriptor,
			inputValue: any
		) => {
			let newInputValues = {
				...inputValues,
				[descriptor.inputId]: inputValue,
			};

			const filteredEntries = Object.entries(calibrationData).filter(
				([inputId]) => {
					const descriptor = testKitInputDescriptorVOs.find((desc: any) => desc.inputId === inputId);
					return descriptor ? descriptor?.type?.includes('Calibration') || isBehindCalibration(testKitInputDescriptorVOs, descriptor) : true;
				}
			);
			const filteredCalibrationData = Object.fromEntries(filteredEntries);

			// Iterate over each calibration data entry
			for (const [inputId, value] of Object.entries(filteredCalibrationData)) {
				newInputValues[inputId] = value;

				if ((isNaN(value) || value === null) && descriptor.isRequired) {
					if (!missingFields.includes(inputId)) {
						setMissingFields((prev) => [...prev, inputId]);
					}
				} else {
					setMissingFields((prev) => prev.filter((id) => id !== inputId));
				}
			}

			setInputValues(newInputValues);
			updateOutputValuesBasedOnInput(newInputValues, outputValues);

			setMissingFields((prevMissingFields) => {
				let updatedMissingFields = [...prevMissingFields];

				if ((isNaN(inputValue) || inputValue === null || inputValue === '') && descriptor.isRequired) {
					if (!missingFields.includes(descriptor.inputId)) {
						setMissingFields((prev) => [...prev, descriptor.inputId]);
					}
				} else {
					setMissingFields((prev) =>
						prev.filter((id) => id !== descriptor.inputId)
					);
				}

				if (onMissingFieldsUpdate) {
					onMissingFieldsUpdate(updatedMissingFields);
				}
				return updatedMissingFields;
			});
		};

		const handleInputChange = (
			e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
			descriptor: TestKitInputDescriptor
		) => {
			const value = e.target.value;
			let inputValue;

			if (value !== '') {
				inputValue = parseFloat(value);

				if (isNaN(inputValue)) {
					inputValue = null;
				}
			} else {
				inputValue = null;
			}

			updateInputValue(descriptor, inputValue);
		};

		//Evaluates both formulae, and then checks the values against the current risk score weights
		function evaluateRiskScore(
			newInputValues: Record<string, number>,
			currentOutputValues: Record<string, number>
		): number {
			const inputDescriptors = testKitInputDescriptorVOs;
			if (riskScoreVO && inputDescriptors) {

				const { requiredFields, optionalFields } = separateRequiredOptionalFields(inputDescriptors);

				let formulaResultX = evaluationConst(
					riskScoreVO.formulaXAxis,
					newInputValues,
					currentOutputValues,
					false,
					requiredFields,
					optionalFields
				);
				let formulaResultY = evaluationConst(
					riskScoreVO.formulaYAxis,
					newInputValues,
					currentOutputValues,
					false,
					requiredFields,
					optionalFields
				);

				if (Number.isNaN(formulaResultX) || Number.isNaN(formulaResultY)) {
					return Number.NaN;
				}

				let weightX = 0;
				let weightY = 0;

				if (formulaResultX < riskScoreVO.lowXaxisValue) {
					weightX = riskScoreVO.lowXaxisWeight;
				} else if (formulaResultX < riskScoreVO.highXaxisValue) {
					weightX = riskScoreVO.midXaxisWeight;
				} else {
					weightX = riskScoreVO.highXaxisWeight;
				}

				if (formulaResultY < riskScoreVO.lowYaxisValue) {
					weightY = riskScoreVO.lowYaxisWeight;
				} else if (formulaResultY < riskScoreVO.highYaxisValue) {
					weightY = riskScoreVO.midYaxisWeight;
				} else {
					weightY = riskScoreVO.highYaxisWeight;
				}

				return weightX + weightY;
			}
			return Number.NaN;
		}

		

		useEffect(() => {
			if (!calibrationData) return;
			const serialized = JSON.stringify(calibrationData);
			const prevSerialized = JSON.stringify(prevCalibrationDataRef.current);
			const descriptorsSerialized = JSON.stringify(testKitInputDescriptorVOs);
			const prevDescriptorsSerialized = JSON.stringify(prevInputDescriptorsRef.current);
			if (serialized === prevSerialized && descriptorsSerialized === prevDescriptorsSerialized) return;	
			prevCalibrationDataRef.current = JSON.parse(serialized);
			prevInputDescriptorsRef.current = JSON.parse(descriptorsSerialized);
			const filteredEntries = Object.entries(calibrationData).filter(
				([inputId]) => {
					const descriptor = testKitInputDescriptorVOs.find((desc: any) => desc.inputId === inputId);
					return descriptor ? descriptor?.type?.includes('Calibration') || isBehindCalibration(testKitInputDescriptorVOs, descriptor) : true;
				}
			);
			const filteredCalibrationData = Object.fromEntries(filteredEntries);
			setInputValues((prevInputValues) => {
			const newInputValues = { ...prevInputValues, ...filteredCalibrationData };
			updateOutputValuesBasedOnInput(newInputValues, outputValuesRef.current);
			return newInputValues;
		});
		}, [calibrationData, testKitInputDescriptorVOs]);

		let backgroundButtonDisplayed = false;

		// Filter out objects with type field and create input fields for the rest
		const inputFields = testKitInputDescriptorVOs
			.filter(
				(descriptor: { type: string }) =>
					(!descriptor?.type?.includes('Calibration')) &&
					!isBehindCalibration(testKitInputDescriptorVOs, descriptor)
			)
			.sort(
				backgroundsToTheEndSorter
			)
			.map((descriptor: TestKitInputDescriptor) => {
				let labelContent = descriptor.inputName;
				const isError =
					missingFields.includes(descriptor.inputId) &&
					inputValues[descriptor.inputId] === null;

				if (descriptor.unit && descriptor.unit.trim() !== '') {
					labelContent += ` (${descriptor.unit})`;
				}

				if (checkForOptionalBackgroundButton(selectedTestKit, descriptor.inputId)) {

					if (!backgroundsOn && !backgroundButtonDisplayed) {

						backgroundButtonDisplayed = true;

						return (
							<Button
								key={"backgroundOptionalButton"}
								onClick={onBackgroundClick}
								addStyle="!w-[193px] !h-[48px] !font-bold add-another-test"
							>
								+ Background RLU 
							</Button>
						);

					} else if (backgroundsOn) {
						//Let the if flow past and display the field normally
					} else {
						return (<></>);
					}
				}

				const controlledValue = inputValues[descriptor.inputId] ?? '';

				return (
					<div key={descriptor.inputId} className="flex space-x-2 items-center !mb-4">
						<TextField
							required={descriptor.isRequired}
							label={labelContent}
							error={isError}
							value={controlledValue ?? ''}
							sx={{
								'.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
									{
										borderRadius: '2px',
									},
									
									'& .MuiInputLabel-root.MuiInputLabel-shrink':  {
											maxWidth: labelContent.length >= 10 ? '130%' : '100%',
											backgroundColor: CONSTANTS.TEXT_INPUT_LABEL_BACKGROUND,
											padding: '0.25rem 0.35rem 0 0.35rem',
											marginLeft: '-0.35rem',
											marginTop: '-0.10rem',
											borderRadius: '5px',
										},
							}}
							type={'number'}
							onChange={(e) => handleInputChange(e, descriptor)}
							inputProps={{
								style: {
									height: '15px',
									fontFamily: 'Open Sans, sans-serif',
									width: '172px',
								},
							}}
							InputLabelProps={{
								shrink:
									inputValues[descriptor.inputId] !== undefined && inputValues[descriptor.inputId] !== null,
							}}
						/>

						{ descriptor.readPM && (<ReadButton
							descriptor={descriptor}
							isConnected={isConnected}
							siteId={siteId}
							currProgress={progress}
							updateProgressBar={updateProgressBar}
							updateInputValue={updateInputValue}
							cleanupMemory={cleanupMemory}
							setShowDialog={setShowDialog}
							setTimer={(timeout?: number) => setClearTimeoutId(timeout ?? null)}
						/>)}
					</div>
				);
			});

		function onBackgroundClick() {
			setBackgroundsOn(true);
		}

		//Shuffles backgrounds for AccuCount kits to the end (keeping order if there are multiple)
		function backgroundsToTheEndSorter(a: any, b: any) {
			if (b?.inputId === "bRLUAMP" || b?.inputId === "bRLUATP") {
				return -1;
			} else if (a?.inputId === "bRLUAMP" || a?.inputId === "bRLUATP") {
				return 1;
			} else {
				return 0;
			}
		}

		const calculateAlarmType = (value: number, descriptor?: any): number => {
			let hiValue, hiHiValue, loValue, loLoValue;

			if (typeof descriptor !== 'undefined') {
				hiValue = descriptor.hiAlarm;
				hiHiValue = descriptor.hiHiAlarm;
				loValue = descriptor.loAlarm;
				loLoValue = descriptor.loLoAlarm;
			}

			if (
				typeof hiValue === 'undefined' &&
				typeof hiHiValue === 'undefined' &&
				typeof loValue === 'undefined' &&
				typeof loLoValue === 'undefined'
			) {
				return 10;
			}

			let isOneWayAlarm =
				typeof loValue === 'undefined' && typeof loLoValue === 'undefined';
			let isTwoWayAlarm =
				typeof loValue !== 'undefined' || typeof loLoValue !== 'undefined';

			if (isTwoWayAlarm) {
				if (typeof loLoValue !== 'undefined' && value <= loLoValue) {
					return 1;
				} else if (typeof loValue !== 'undefined' && value <= loValue) {
					return 2;
				}
			}

			if (isOneWayAlarm || isTwoWayAlarm) {
				if (typeof hiHiValue !== 'undefined' && value >= hiHiValue) {
					return 1;
				} else if (typeof hiValue !== 'undefined' && value >= hiValue) {
					return 2;
				}
			}

			return 3;
		};

		//Provides handling for risk score and other alarms as needed before plugging into calculateAlarmType.
		function determineAlarmType(value: number, fieldId: string, isRiskScore?: boolean) {
			if (!alarmSettingsPerLocation || !selectedLocationDetails?.name || !testKitId) {
				return 10;
			}
			
			let descriptor = alarmSettingsPerLocation.find((alarm: SiteAlarmSetting) => 
				alarm.locationName === selectedLocationDetails.name && 
				alarm.testKitId === testKitId &&
				alarm.fieldId === fieldId
			);

			return (isRiskScore && riskScoreVO) ? calculateAlarmType(value, {
				hiAlarm: riskScoreVO.hiAlarmValue,
				hiHiAlarm: riskScoreVO.hiHIAlarmValue,
				loAlarm: riskScoreVO.loAlarmValue,
				loLoAlarm: riskScoreVO.loLoAlarmValue,
			})
				: calculateAlarmType(
					value,
					descriptor,
				);
		}

		const outPutFields = testKitOutputDescriptorVOs
			.filter(
				(descriptor: TestKitOutputDescriptor) =>
					descriptor.outputId !== 'LAAF' &&
					descriptor.outputName !== 'LAAF'
			)
			.map((descriptor: TestKitOutputDescriptor) => {
				const outputValue = outputValues[descriptor.outputId];

				let labelContent = descriptor.outputName;

				if (descriptor.unit && descriptor.unit.trim() !== '') {
					labelContent += ` (${descriptor.unit})`;
				}

				const alarmType = determineAlarmType(
					outputValue,
					descriptor.outputId,
					(descriptor.outputName === "Risk Score"),
				);

				if (!isNaN(outputValue) && descriptor.hide === false) {
					return (
						<div className="flex space-x-2 items-center !mb-3">
							<TextFieldExpanded
								key={descriptor.id}
								label={labelContent}
								value={
									formatNumber(
										outputValues[
											descriptor.outputId
										]?.toString()
									) || '0'
								}
								alarmType={alarmType}
								disabled={true}
								isEditingOutput={true}
							/>
						</div>
					);
				}
		return null;
			})
		.filter(Boolean);

		const handleSelectedTestKit = (value: string) => {
			setSelectedTestKit(value);
		};

		const handleDateChange = (value: unknown, keyboardInputValue?: string) => {
			if (dayjs.isDayjs(value) || value === null) {
				setSelectedDate(value as Dayjs);
			}
		};

		const handleDescriptionChange = (
			e: React.ChangeEvent<HTMLInputElement>
		) => {
			setDescriptionValue(e.target.value);
		};

		const handleSelectedLocation = (value: string) => {
			setSelectedLocation(value);

			setInputValues({});
			setOutputValues({});
		};

		useEffect(() => {
			const details = locations.find((loc) => loc.name === selectedLocation);
			setSelectedLocationDetails(details ?? null);
			const testKits = details?.testKitDescriptors?.map(
				(tk) => tk.testKitName
			);
			setAvailableTestKits(testKits || []);
		}, [selectedLocation]);

		useEffect(() => {
			if (availableTestKits.length === 1) {
				setSelectedTestKit(availableTestKits[0]);
			} else {
				setSelectedTestKit('');
			}
		}, [availableTestKits]);

		useEffect(() => {
			if (locations.length === 1) {
				setSelectedLocation(locations[0].name);
			}
		}, [locations]);

		const updateCachedData = async (newDataArray: any, newID: any = undefined) => {
			const user = localStorage.getItem('currentUser') ?? 'unknown_user';

			// Retrieve the current data points for the user and site
			let dataPoints = await getTestResults(`dataPoints_all_${siteId}`, user);

			// Initialize dataPoints as an array if it's not already an array
			if (!Array.isArray(dataPoints)) {
				dataPoints = [];
			}

			// Iterate over newDataArray and add each item to the dataPoints array
			newDataArray.forEach((newData: any) => {
				const restData = {
					...newData,
					testKitId: testKitId,
					new_entry: newID ?? `${Date.now()}`,
				};
				newData.id = restData.new_entry;
				dataPoints.push(restData);
			});

			// Save the updated array back to the cache
			await saveTestResults(`dataPoints_all_${siteId}`, dataPoints, user);

		};

		const updateCachedDataAll = async (newDataArray: any, newID: any = undefined) => {
			const user = localStorage.getItem('currentUser') ?? 'unknown_user';

			// Retrieve the current data points for the user and site
			let dataPoints = await getTestResults(`dataPoints_all_${siteId}`, user);

			// Initialize dataPoints as an array if it's not already an array
			if (!Array.isArray(dataPoints)) {
				dataPoints = [];
			}

			// Iterate over newDataArray and add each item to the dataPoints array
			newDataArray.forEach((newData: any) => {
				const restData = {
					...newData,
					testKitId: testKitId,
					new_entry: newID ?? `${Date.now()}`,
				};
				dataPoints.push(restData);
			});

			// Save the updated array back to the cache
			await saveTestResults(`dataPoints_all_${siteId}`, dataPoints, user);
		};

		const createTestResultDataList = (deviceSN?: string, pbmID?: string) => {
			const outputDataList = testKitOutputDescriptorVOs
				.map((descriptor: any) => {
					const outputValue = outputValues[descriptor.outputId];
					const alarmType = determineAlarmType(outputValue, descriptor.outputId, (descriptor.outputId === "RiskScore"));
					const newTestResultData = !(Number.isNaN(outputValue)) ? {
						outputAlarm: alarmType,
						outputId: descriptor.outputId,
						outputName: descriptor.outputName,
						outputType: 'Output',
						outputValue: outputValue.toString(),
						samplePointId: selectedLocationDetails?.id,
						samplePointName: selectedLocationDetails?.name,
						testkitName: selectedTestKit,
						siteId: siteId,
						siteName: siteName,
						timestamp: selectedDate?.valueOf() || dayjs().valueOf(),
						note: descriptionValue,
						deviceSerial: deviceSN,
						pbmId: pbmID,
						operatorEmail: localStorage.getItem('currentUser') || 'unknown',
						displayName: descriptor.outputName,
					} : null;

					return newTestResultData;
				})
				.concat(
					testKitInputDescriptorVOs.map(
						//Inputs. For now this keeps the 'form' of outputs for storing data, while setting 'outputType' to 'Input'.
						(descriptor: any) => {
							const inputValue = inputValues[descriptor.inputId];
							const newTestResultData = {
								outputAlarm: 10,
								outputId: descriptor.inputId,
								outputName: descriptor.inputName,
								outputType: 'Input',
								outputValue: inputValue ? inputValue.toString() : '0',
								samplePointId: selectedLocationDetails?.id,
								samplePointName: selectedLocationDetails?.name,
								testkitName: selectedTestKit,
								siteId: siteId,
								siteName: siteName,
								timestamp:
									selectedDate?.valueOf() || dayjs().valueOf(),
								note: descriptionValue,
								deviceSerial: deviceSN,
								pbmId: pbmID,
								operatorEmail: localStorage.getItem('currentUser') || 'unknown',
								displayName: descriptor.inputName,
							};

							return newTestResultData;
						}
					)
				)
				.filter((data: any) => data !== null);
			return outputDataList;
		};

		const saveData = async () => {
			const { deviceID: deviceSN, pbmId: pbmID } = getDeviceIds();
			setLoading(true);
			setIsSaving(true);
			setIsTestLoading(true);

			const missing: string[] = [];

			const mappedInputData = testKitInputDescriptorVOs.map(
				(descriptor: {
					inputId: string;
					inputName: any;
					isRequired: boolean;
				}) => {
					const inputValue = inputValues[descriptor.inputId];

					if (
						(inputValue === undefined ||
							inputValue === null ||
							isNaN(inputValue)) &&
						descriptor.isRequired
					) {
						missing.push(descriptor.inputId);
					}

					setMissingFields((prevMissing) => {
						const updatedMissing = [...prevMissing, ...missing];
						if (onMissingFieldsUpdate) {
							onMissingFieldsUpdate(updatedMissing);
						}
						return updatedMissing;
					});

					return {
						name: descriptor.inputId,
						value: inputValue ? inputValue.toString() : '0',
						type: 'Input',
						alarmType: 10,
					};
				}
			);

			const mappedOutputData = Object.entries(outputValues)
				.map(([key, value]) => {
					// Find the descriptor for the current key
					const descriptor = testKitOutputDescriptorVOs.find(
						(d: { outputId: string }) => d.outputId === key
					);

					// Check if descriptor and value are defined
					if (descriptor && value !== undefined) {
						const outputValue = outputValues[descriptor.outputId];

						// Determine the alarm type
						const alarmType = determineAlarmType(
							outputValue,
							descriptor.outputId,
							descriptor.outputId === "RiskScore",
						);

						// Return the mapped object
						return {
							name: descriptor.outputId,
							value: value.toString(),
							type: 'Output',
							alarmType: alarmType,
						};
					}
					// If descriptor or value is undefined, return a default or null object
					return null;
				})
				.filter((item) => item !== null); // Filter out null entries

			const testKitMeasurementResultVOs: TestKitMeasurementResultVO = {
				samplePointId: selectedLocationDetails?.id ?? 0,
				testKitId: testKitId,
				timeStamp: selectedDate?.valueOf() ?? dayjs().valueOf(),
				status: 'Active',
				testKitMeasurementResultValueVOs: [
					...mappedInputData,
					...mappedOutputData,
				].filter((x): x is TestKitMeasurementResultValueVO => x !== null),
			};

			const data: DataPointTestKitMeasurement = {
				timeStamp: selectedDate?.valueOf() || dayjs().valueOf(),
				samplePointId: selectedLocationDetails?.id || 0,
				status: 'Active',
				addedBy: userId || 0,
				note: descriptionValue,
				testKitMeasurementResultVOs: [testKitMeasurementResultVOs],
				supplementalDataValueVOs: [],
				photonMasterId: deviceSN ?? undefined,
				pbmId: pbmID ?? undefined,
			};
			const username = localStorage.getItem('currentUser') || 'unknown';
			const notLocalStorage = await getSwitchStateFromDB(
				'user_switch_state',
				username
			);

			let localFlag = localStorage.getItem('localStorage') ?? 'false';

			try {
				if (
					missing.length === 0 &&
					(notLocalStorage === true || notLocalStorage === undefined)
				) {
					let response: any;
					if (localFlag !== 'true') {
						response = await ApiService.postDataPointResult(data);
					}

					const newID = response?.data?.id;
					const testResultDataList = createTestResultDataList(deviceSN, pbmID);

					// Call the function to update the cache with the new data
					updateCachedData(testResultDataList, newID)
						.then(() => {
							console.log(
								'The cache has been updated.'
							);
						})
						.catch((error) => {
							console.error('Error updating the cache:', error);
						});

					updateCachedDataAll(testResultDataList, newID)
						.then(() => {
							console.log(
								'The cache has been updated.'
							);
						})
						.catch((error) => {
							console.error('Error updating the cache:', error);
						});

					setIsUploaded(true);
					sendPopUpMessage(
						'Success',
						`Test saved. Refreshing the site table...`,
						false
					);
					setIsSaving(false);
					addEventLog("Save Test Success", "Tests", siteId);
					setTimeout(() => {
						if (onTestDone) {
							setIsTestLoading(false);
							onTestDone(data.samplePointId);
						}
					}, 3000);
				}
			} catch (error) {

				console.log(error);

				const testResultDataList = createTestResultDataList(deviceSN, pbmID);
				const newID = `${Date.now()}`;

				// Call the function to update the cache with the new data
				updateCachedData(testResultDataList, newID)
					.then(() => {
						console.log(
							'The cache has been updated.'
						);
					})
					.catch((error) => {
						console.error('Error updating the cache:', error);
					});

				updateCachedDataAll(testResultDataList, newID)
					.then(() => {
						console.log(
							'The cache has been updated.'
						);
					})
					.catch((error) => {
						console.error('Error updating the cache:', error);
					});

				await saveSyncCache(
					{
					id: newID,
					locationFK: data.samplePointId,
					operatorId: data.addedBy,
					testKitId: data.testKitMeasurementResultVOs[0].testKitId ?? 0,
					note: data.note ?? "",
					timestamp: data.timeStamp,
					resultId: data.testKitMeasurementResultVOs[0].resultId ?? 0,
					deviceSerial: data.photonMasterId ?? "",
					valueDatas:
						data.testKitMeasurementResultVOs[0].testKitMeasurementResultValueVOs.map(
						(valueVO) => ({
							outputId: valueVO.name,
							outputAlarm: valueVO.alarmType ?? 10,
							outputValue: valueVO.value.toString() ?? "0",
							type: valueVO.type,
						})
						),
					action: "CREATE",
					},
					"dataPointSyncCache"
				);

				setIsUploaded(true);
				if (navigator.onLine) { 
					sendPopUpMessage('Result saved locally', `Cloud sync is temporarily unavailable. This result will be queued to sync until restored.`);
				} else {
					sendPopUpMessage('Success', `Changes saved offline!`);
				}
				setIsSaving(false);
				addEventLog("Save Test Success", "Tests", siteId);
				setTimeout(() => {
					if (onTestDone) {
						onTestDone(data.samplePointId);
						setIsTestLoading(false);
					}
				}, 3000);
				// Register a background sync event with the service worker
				if ('serviceWorker' in navigator && 'SyncManager' in window && localFlag !== 'true') {
					try {
						const sw = await navigator.serviceWorker.ready;
						await sw.sync.register('syncTestResults');
						console.log('Sync event registered');
					} catch (err) {
						console.error('Failed to register sync:', err);
						addEventLog("Save Test Failed", "Tests", siteId, '', "Sync registration failed in offline if case");
					}
				}
				
			}

			setLoading(false);
		};

		const handleClose = () => {
			setShowDialog(false);
		};

		function checkPBMSerialNum(): string | null {
			if (pbmSerialNumber && typeof pbmSerialNumber === "string") {
				return pbmSerialNumber.startsWith("PBM1") ? pbmSerialNumber : "PBM1" + pbmSerialNumber;
			} else {
				return null;
			}
		}

		return (
			<div className="flex justify-center mb-2 !bg-tertiary-grey-2">
				<div className={`bg-white w-[1108px] flex px-2 py-2 ${addStyle}`}>
					<div className="flex-1 px-4 py-2 text-left  test">
						{/* Test Info */}
						<h3 className="mb-2 text-xl font-semibold leading-tight mb-6 !tracking-[0.17px]">
							Test {index}
						</h3>
						<Autocomplete
							value={locationOptions.find((o) => o === selectedLocation) ?? null}
							onChange={(_, option) => handleSelectedLocation(option ?? "")}
							options={locationOptions}
							getOptionLabel={(option) => option}
							isOptionEqualToValue={(option, val) => option === val}
							size="small"
							className={`!w-[332px] mb-4 location`}
							renderInput={(params) => (
								<TextField
								{...params}
								label="Location "
								sx={{
									'.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
										borderRadius: '2px',
									},
									'.MuiInputLabel-root': {
										transform: 'translate(14px, 10px) scale(1)',
									},
									'.MuiInputLabel-root.MuiInputLabel-shrink': {
										transform: 'translate(14px, -9px) scale(0.75)',
									},
								}}
								inputProps={{
									...params.inputProps,
									className: `${params.inputProps.className ?? ""} !text-[16px]`,
									style: {
										fontFamily: 'Open Sans, sans-serif',
									},
								}}
								/>
							)}
						/>

						{/* Test Kit Dropdown */}
						<DropDownFields
							label={'Test Kit'}
							value={selectedTestKit}
							onChange={handleSelectedTestKit}
							options={availableTestKits}
							addStyle="!w-[332px] mb-4 !text-[16px] !leading-[25px] test-kit"
							height={40}
							disabled={selectedLocation === 'Please Select'}
						/>

						<DatePickerExpanded
							label="Sampled"
							value={selectedDate}
							onChange={handleDateChange}
							addStyle="!w-[332px] !mb-4 sampled"
							height={40}
						/>
						<MultipleLineTextField
							label="Notes"
							value={descriptionValue}
							onChange={handleDescriptionChange}
							rows={2}
							addStyle="!mb-4 notes"
						/>

						<Button
							onClick={onAddTestClick}
							addStyle="!w-[193px] !h-[48px] !font-bold add-another-test"
						>
							+ Add Another Test
						</Button>
					</div>

					<div className="flex-1 px-4 py-2 text-left  input">
						{/* Input */}
						<h3 className="mb-2 text-xl font-semibold leading-tight mb-6 !tracking-[0.17px] Input">
							Inputs
						</h3>
						{/* ATP Calibration */}
						{inputFields}
						{/* Additional content */}
					</div>

					<div className="flex-1 px-4 py-2 text-left  flex flex-col justify-between results">
						{/* Results */}
						<div className="flex justify-between items-start">
							<h3 className="text-xl font-semibold leading-tight !tracking-[0.17px]">
								Results
							</h3>
							{/* Remove Test Button */}
							<IconButton
								onClick={openDeleteDialog}
								sx={{ p: 0, mr: '5px', mt: '2px' }}
							>
								<img
									src="/assets/icons/Close.svg"
									alt="Remove"
									className="w-5 h-5 md:block hidden"
								/>
							</IconButton>
							<DeleteConfirmationDialog
								open={isDeleteDialogOpen}
								onClose={closeDeleteDialog}
								onConfirm={handleDelete}
							/>
						</div>
						<div className="flex flex-col flex-1">
							<div className="flex mt-3 gap-4 !h-full">
								<div className="flex-1 mt-3">
				{outPutFields.length === 0 ? (
										<div
											className="flex items-center justify-center"
											style={{
												backgroundColor: '#f4f8f9',
						width: '280px',
												color: '#556670',
												fontFamily: 'Jost',
												fontSize: '16px',
												fontWeight: '400',
												borderRadius: '4px',
												padding: '10px 5px 10px 5px ', 
						boxSizing: 'border-box',
												textAlign: 'left',
											}}
										>
											<p>
												Results will display when all <br></br>
						<b>Calibration</b> and <b>Test Inputs</b> are filled.
											</p>
										</div>
									) : (
										<div>{outPutFields.slice(0, 5)}</div>
									)}
								</div>

								<div
									className="flex-1 mt-3"
									style={{ height: '288px' }}
								>
									{outPutFields.slice(5)}
								</div>
							</div>
							<div className="flex justify-end  space-x-2 ">
								<Button
									style={{
										maxWidth: '84px',
										maxHeight: '84px',
										minWidth: '40px',
										minHeight: '40px',
										display: isUploaded ? 'none' : 'block',
										fontFamily: 'Inter',
										fontSize: '14px',
										fontWeight: 600,
										lineHeight: '17px',
										letterSpacing: '0.46px',
										textAlign: 'left',
										color: '#556670', // Assuming this is the text color you want to apply
									}}
									disabled={loading || isTestLoading}
									title="Cancel"
									onClick={openCacelTestDialog}
									addStyle="!h-[40px] !bg-tertiary-grey-2 !text-primary-grey !font-inter !text-[14px] !font-bold"
								>
									Cancel
								</Button>
								<PrimaryButton
									onClick={saveData}
									addStyle="!w-[138px] !h-[39px] !tracking-[0.46px] save-test flex items-center justify-center"
									disabled={
										(loading && isSaving) ||
										missingFields.length > 0 ||
										selectedTestKit === '' ||
										selectedLocation === '' ||
										isTestLoading
									}
								>
									{loading && isSaving ? (
										<CircularProgress
											size={20}
											color="primary"
											className="!ml-2"
										/>
									) : (
										`Save Test ${index}`
									)}
								</PrimaryButton>

								<CancelConfirmationDialog
									open={isCancelTestDialogOpen}
									onClose={closeCancelTestDialog}
									onConfirm={handleCancelTest}
								/>
							</div>
						</div>
					</div>
				</div>
				{showDialog && <LoadingPopup open={showDialog} progress={progress} type={deviceStatus} onClose={handleClose}/>}
			</div>
		);
	};

	export default Test;
